"use client";

import { useEffect, useSyncExternalStore } from "react";
import { RefreshCw } from "lucide-react";
import { usePathname } from "next/navigation";
import {
  isPageStale,
  isStaleBannerVisible,
  pageStaleReason,
  staleHandledInPlacePath,
  subscribePageStale,
} from "@/lib/virtualspace/stale-deployment-store";

/**
 * Offers a reload once this tab calls a server action the live deployment
 * no longer has. The watcher is imported after mount because it pulls in
 * Sonner, which the root layout keeps off the marketing critical path.
 */
export function StaleDeploymentGuard() {
  const pathname = usePathname();
  // Subscribe to both inputs so either change re-renders the banner.
  useSyncExternalStore(subscribePageStale, isPageStale, () => false);
  useSyncExternalStore(subscribePageStale, staleHandledInPlacePath, () => null);
  const reason = useSyncExternalStore(subscribePageStale, pageStaleReason, () => null);
  const stale = isStaleBannerVisible(pathname);

  useEffect(() => {
    let uninstall: (() => void) | undefined;
    let cancelled = false;
    void import("@/lib/virtualspace/stale-deployment-watch").then((mod) => {
      if (!cancelled) uninstall = mod.installStaleDeploymentWatch();
    });
    return () => {
      cancelled = true;
      uninstall?.();
    };
  }, []);

  if (!stale) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex justify-center px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
      <div
        role="alert"
        data-testid="stale-deployment-banner"
        className="vs-glass-floating pointer-events-auto flex w-full max-w-xl flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg"
      >
        <RefreshCw className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <p className="min-w-0 flex-1">
          {reason === "unexpected-response" ? (
            <>
              <span className="font-medium">We lost track of that request.</span>{" "}
              <span className="text-muted-foreground">
                The server didn&apos;t answer clearly, so your last action may
                not have gone through. Reload to check before trying again.
              </span>
            </>
          ) : (
            <>
              <span className="font-medium">This page is out of date.</span>{" "}
              <span className="text-muted-foreground">
                We updated the app while it was open, so your last action
                didn&apos;t go through. Reload to continue.
              </span>
            </>
          )}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="vs-btn-solid inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
